/**
 * Platform Icons — SVG icon library for messaging platforms.
 *
 * All icons: 24x24 viewBox, stroke-based, 1.5px weight, rounded caps/joins.
 * Matches the nav icon style in consumer-navigation.ts.
 */

export type PlatformId =
  | "whatsapp"
  | "telegram"
  | "discord"
  | "slack"
  | "signal"
  | "imessage"
  | "matrix"
  | "teams";

export interface PlatformMeta {
  id: PlatformId;
  name: string;
  svg: string;
  description: string;
  color: string;
}

const a = `viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"`;

export const PLATFORMS: Record<PlatformId, PlatformMeta> = {
  whatsapp: {
    id: "whatsapp",
    name: "WhatsApp",
    svg: `<svg ${a}><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>`,
    description: "Chat via WhatsApp",
    color: "var(--channel-whatsapp)",
  },
  telegram: {
    id: "telegram",
    name: "Telegram",
    svg: `<svg ${a}><path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4z"/></svg>`,
    description: "Chat via Telegram bot",
    color: "var(--channel-telegram)",
  },
  discord: {
    id: "discord",
    name: "Discord",
    svg: `<svg ${a}><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><circle cx="12" cy="17" r=".5" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="10"/></svg>`,
    description: "Chat via Discord bot",
    color: "var(--channel-discord)",
  },
  slack: {
    id: "slack",
    name: "Slack",
    svg: `<svg ${a}><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>`,
    description: "Chat via Slack app",
    color: "var(--channel-slack)",
  },
  signal: {
    id: "signal",
    name: "Signal",
    svg: `<svg ${a}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><rect x="10" y="9" width="4" height="5" rx="2"/><line x1="12" y1="14" x2="12" y2="16"/></svg>`,
    description: "Chat via Signal",
    color: "var(--channel-signal)",
  },
  imessage: {
    id: "imessage",
    name: "iMessage",
    svg: `<svg ${a}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`,
    description: "Chat via iMessage",
    color: "var(--channel-imessage)",
  },
  matrix: {
    id: "matrix",
    name: "Matrix",
    svg: `<svg ${a}><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>`,
    description: "Chat via Matrix protocol",
    color: "var(--channel-matrix)",
  },
  teams: {
    id: "teams",
    name: "Teams",
    svg: `<svg ${a}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`,
    description: "Chat via Microsoft Teams",
    color: "var(--channel-teams)",
  },
};

export const PLATFORM_LIST: PlatformMeta[] = Object.values(PLATFORMS);

/* ─────────────────────────────────────────────────────────────
 * Extended Integration List — used by the landing page only.
 * Includes messaging + email, calendar, storage, dev tools, custom APIs.
 * Existing PLATFORMS / PLATFORM_LIST is left untouched so other
 * consumers (chat.ts, channels.ts, sessions.ts) keep working.
 * ───────────────────────────────────────────────────────────── */

export type IntegrationCategory =
  | "messaging"
  | "email"
  | "platform"
  | "ai"
  | "calendar"
  | "storage"
  | "productivity"
  | "devtools"
  | "custom";

export interface IntegrationMeta {
  id: string;
  name: string;
  svg: string;
  color: string;
  category: IntegrationCategory;
  available: boolean;
}

export const INTEGRATION_LIST: IntegrationMeta[] = [
  /* ── Messaging (reuse existing SVGs) ── */
  { id: "whatsapp", name: "WhatsApp", svg: PLATFORMS.whatsapp.svg, color: "var(--channel-whatsapp)", category: "messaging", available: true },
  { id: "telegram", name: "Telegram", svg: PLATFORMS.telegram.svg, color: "var(--channel-telegram)", category: "messaging", available: true },
  { id: "discord", name: "Discord", svg: PLATFORMS.discord.svg, color: "var(--channel-discord)", category: "messaging", available: true },
  { id: "slack", name: "Slack", svg: PLATFORMS.slack.svg, color: "var(--channel-slack)", category: "messaging", available: true },
  { id: "signal", name: "Signal", svg: PLATFORMS.signal.svg, color: "var(--channel-signal)", category: "messaging", available: false },
  { id: "imessage", name: "iMessage", svg: PLATFORMS.imessage.svg, color: "var(--channel-imessage)", category: "messaging", available: false },
  { id: "matrix", name: "Matrix", svg: PLATFORMS.matrix.svg, color: "var(--channel-matrix)", category: "messaging", available: false },
  { id: "teams", name: "Teams", svg: PLATFORMS.teams.svg, color: "var(--channel-teams)", category: "messaging", available: false },

  /* ── Email ── */
  { id: "gmail", name: "Gmail",
    svg: `<svg ${a}><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 4L12 13 2 4"/></svg>`,
    color: "var(--integration-gmail)", category: "email", available: false },
  { id: "outlook", name: "Outlook",
    svg: `<svg ${a}><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 4l10 8 10-8"/><line x1="2" y1="20" x2="9" y2="13"/><line x1="22" y1="20" x2="15" y2="13"/></svg>`,
    color: "var(--integration-outlook)", category: "email", available: false },
  { id: "yahoo", name: "Yahoo Mail",
    svg: `<svg ${a}><path d="M2 4l5.5 8"/><path d="M22 4l-5.5 8"/><path d="M12 12v8"/><circle cx="12" cy="12" r="1" fill="currentColor" stroke="none"/></svg>`,
    color: "var(--integration-yahoo)", category: "email", available: false },
  { id: "protonmail", name: "ProtonMail",
    svg: `<svg ${a}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M8 10l4 3 4-3"/></svg>`,
    color: "var(--integration-protonmail)", category: "email", available: false },

  /* ── Calendar ── */
  { id: "gcal", name: "Google Calendar",
    svg: `<svg ${a}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M9 14l2 2 4-4"/></svg>`,
    color: "var(--integration-gcal)", category: "calendar", available: true },
  { id: "ocalendar", name: "Outlook Calendar",
    svg: `<svg ${a}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><circle cx="12" cy="15" r="2"/></svg>`,
    color: "var(--integration-ocalendar)", category: "calendar", available: false },
  { id: "applecal", name: "Apple Calendar",
    svg: `<svg ${a}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="14" x2="8" y2="18"/><line x1="12" y1="14" x2="12" y2="18"/></svg>`,
    color: "var(--integration-applecal)", category: "calendar", available: false },

  /* ── Cloud Storage ── */
  { id: "gdrive", name: "Google Drive",
    svg: `<svg ${a}><path d="M9 3L1 15h6l8-12z"/><path d="M15 3l8 12h-6L9 3z"/><path d="M1 15h22l-3 6H4z"/></svg>`,
    color: "var(--integration-gdrive)", category: "storage", available: true },
  { id: "dropbox", name: "Dropbox",
    svg: `<svg ${a}><path d="M12 2L5 7l7 5-7 5 7 5 7-5-7-5 7-5z"/></svg>`,
    color: "var(--integration-dropbox)", category: "storage", available: false },
  { id: "onedrive", name: "OneDrive",
    svg: `<svg ${a}><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/></svg>`,
    color: "var(--integration-onedrive)", category: "storage", available: false },

  /* ── Productivity (Google Workspace) ── */
  { id: "gsheets", name: "Google Sheets",
    svg: `<svg ${a}><rect x="3" y="2" width="18" height="20" rx="2"/><line x1="7" y1="8" x2="17" y2="8"/><line x1="7" y1="12" x2="17" y2="12"/><line x1="7" y1="16" x2="13" y2="16"/><line x1="12" y1="5" x2="12" y2="19"/></svg>`,
    color: "var(--integration-gsheets)", category: "productivity", available: true },
  { id: "gdocs", name: "Google Docs",
    svg: `<svg ${a}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="14" y2="17"/></svg>`,
    color: "var(--integration-gdocs)", category: "productivity", available: true },
  { id: "gslides", name: "Google Slides",
    svg: `<svg ${a}><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/><polygon points="10 7 10 13 15 10"/></svg>`,
    color: "var(--integration-gslides)", category: "productivity", available: true },
  { id: "gforms", name: "Google Forms",
    svg: `<svg ${a}><rect x="4" y="2" width="16" height="20" rx="2"/><circle cx="9" cy="9" r="1" fill="currentColor" stroke="none"/><line x1="12" y1="9" x2="17" y2="9"/><circle cx="9" cy="13" r="1" fill="currentColor" stroke="none"/><line x1="12" y1="13" x2="17" y2="13"/><circle cx="9" cy="17" r="1" fill="currentColor" stroke="none"/><line x1="12" y1="17" x2="17" y2="17"/></svg>`,
    color: "var(--integration-gforms)", category: "productivity", available: true },

  /* ── Dev Tools ── */
  { id: "notion", name: "Notion",
    svg: `<svg ${a}><path d="M4 4h10l6 6v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/><path d="M14 4v6h6"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="12" y2="17"/></svg>`,
    color: "var(--integration-notion)", category: "devtools", available: false },
  { id: "github", name: "GitHub",
    svg: `<svg ${a}><path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/></svg>`,
    color: "var(--integration-github)", category: "devtools", available: false },
  { id: "jira", name: "Jira",
    svg: `<svg ${a}><path d="M12 2L2 12l10 10 10-10L12 2z"/><circle cx="12" cy="12" r="3"/></svg>`,
    color: "var(--integration-jira)", category: "devtools", available: false },
  { id: "linear", name: "Linear",
    svg: `<svg ${a}><circle cx="12" cy="12" r="10"/><path d="M8 12l3 3 5-6"/></svg>`,
    color: "var(--integration-linear)", category: "devtools", available: false },

  /* ── Custom / API ── */
  { id: "webhooks", name: "Webhooks",
    svg: `<svg ${a}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>`,
    color: "var(--integration-webhooks)", category: "custom", available: false },
  { id: "zapier", name: "Zapier",
    svg: `<svg ${a}><line x1="12" y1="2" x2="12" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/><line x1="19.07" y1="4.93" x2="4.93" y2="19.07"/><circle cx="12" cy="12" r="4"/></svg>`,
    color: "var(--integration-zapier)", category: "custom", available: false },
  { id: "restapi", name: "REST API",
    svg: `<svg ${a}><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/><line x1="14" y1="4" x2="10" y2="20"/></svg>`,
    color: "var(--integration-restapi)", category: "custom", available: false },

  /* ── Platforms (supported) ── */
  { id: "macos", name: "macOS",
    svg: `<svg ${a}><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>`,
    color: "var(--integration-macos)", category: "platform", available: true },
  { id: "windows", name: "Windows",
    svg: `<svg ${a}><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="12" y1="3" x2="12" y2="21"/></svg>`,
    color: "var(--integration-windows)", category: "platform", available: true },
  { id: "linux", name: "Linux",
    svg: `<svg ${a}><rect x="2" y="3" width="20" height="18" rx="2"/><polyline points="6 9 10 13 6 17"/><line x1="14" y1="17" x2="18" y2="17"/></svg>`,
    color: "var(--integration-linux)", category: "platform", available: true },

  /* ── AI Capabilities (implemented) ── */
  { id: "tts", name: "Text to Speech",
    svg: `<svg ${a}><polygon points="11 5 6 9 2 9 2 15 6 15 11 19" fill="none"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>`,
    color: "var(--integration-tts)", category: "ai", available: true },
  { id: "websearch", name: "Web Search",
    svg: `<svg ${a}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="3" y1="11" x2="19" y2="11"/><path d="M11 3a12 12 0 0 1 3 8 12 12 0 0 1-3 8 12 12 0 0 1-3-8 12 12 0 0 1 3-8z"/></svg>`,
    color: "var(--integration-websearch)", category: "ai", available: true },
];

/** Non-messaging integrations shown in the Integrations tab (storage, productivity, devtools, calendar) */
export const CONNECTABLE_INTEGRATIONS = INTEGRATION_LIST.filter(
  (i) => i.category === "storage" || i.category === "productivity" || i.category === "devtools" || i.category === "calendar"
);

/** Category labels for display grouping */
export const INTEGRATION_CATEGORY_LABELS: Record<string, string> = {
  storage: "Cloud Storage",
  productivity: "Productivity",
  devtools: "Dev Tools",
  calendar: "Calendar",
};
